import { useTableActionContext } from '@aibees/elephant/context';
import { useCheckLinkedinInputError } from '@aibees/elephant/feature/crawl-request-page-table/components/new-crawl-request-dialog/use-check-linkedin-input-error';
import { FieldValidationType } from '@aibees/elephant/feature/search-filters';
import { useCreateList } from '@aibees/elephant/hooks/labels/use-create-list';
import { useEditList } from '@aibees/elephant/hooks/labels/use-edit-list';
import { useLabelApiType } from '@aibees/elephant/hooks/labels/use-label-api-type';
import {
  Label,
  LabelGrouping,
  LabelType,
  LabelVisibility,
} from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import {
  AccessSettings,
  DialogActions,
  DialogTitle,
  OutlinedInput,
} from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import {
  Dialog,
  DialogContent,
  Fade,
  Stack,
  TextFieldProps,
} from '@mui/material';
import { random } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionBulkSelectColorLabel } from '../action-bulk-select-color-label';
import { useLabelColors } from '../action-bulk-select-color-label/config';
import { SelectLabelGroupingStatus } from '../select-label-grouping-status';
import * as SX from './sx';
import type { CreateActionBulkDialogProps as Props } from './types';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = {
  onMouseDown: stopPropagation,
  sx: SX.dialogPaper,
};

// Helper function to convert PresetVisibility to LabelVisibility
const convertPresetVisibilityToLabelVisibility = (
  presetVisibility: PresetVisibility
): LabelVisibility => {
  switch (presetVisibility) {
    case PresetVisibility.PRIVATE:
      return LabelVisibility.PRIVATE;
    case PresetVisibility.INTERNAL:
      return LabelVisibility.INTERNAL;
    case PresetVisibility.ALL:
      return LabelVisibility.ALL;
    default:
      return LabelVisibility.INTERNAL;
  }
};

// TODO @samanshahmohamadi this component is too complex and needs to be refactored

export const CreateActionBulkDialog = memo((props: Props) => {
  // Destructure props early for clarity
  const {
    className: _className,
    id,
    state,
    firstState,
    title,
    inputTitle,
    finalState,
    hasMoreStep,
    hasDescription,
    hasColor,
    hasGrouping,
    hasLinkedinUrl,
    type,
    grouping: initialGrouping,
    placeholder,
    isEdit,
    defaultValue,
    submitTitle,
    loading,
    hasVisibility = true,
    icon,
    disabledSubmitButton,
    item,
    shouldClearActionContext,
    onClick,
    onClose,
    onBack,
  } = props;

  // State
  const notify = useNotify();
  const { setLabels, setLists, labels, lists } = useTableActionContext();
  const [visibility, setVisibility] = useState<LabelVisibility>(
    item ? item.visibility : LabelVisibility.INTERNAL
  );
  const [name, setName] = useState(defaultValue || '');
  const colors = useLabelColors();
  const isLabel = useMemo(() => type === LabelType.LABEL, [type]);
  const [grouping, setGrouping] = useState<LabelGrouping>(
    initialGrouping || LabelGrouping.COMPANY
  );
  const initialColor = useMemo(() => colors.primary, [colors]);
  const [color, setColor] = useState<string>(item ? item.color : initialColor);
  const [description, setDescription] = useState<string>();
  const [nameError, setNameError] = useState('');
  const [linkedinValue, setLinkedinValue] = useState<string>('');
  const [linkedinErrorText, setLinkedinErrorText] = useState<string>('');
  const [toggleVisibility, setToggleVisibility] = useState(false);
  // Derived values
  const linkedinType = useMemo(
    () =>
      grouping === LabelGrouping.COMPANY
        ? FieldValidationType.COMPANY_LINKEDIN_URL
        : FieldValidationType.PEOPLE_LINKEDIN_URL,
    [grouping]
  );

  const linkedinError = useCheckLinkedinInputError(linkedinValue, linkedinType);
  const addApi = useCreateList();
  const editApi = useEditList(id || '');
  const api = useLabelApiType(addApi, editApi, isEdit);
  const disabled = useBoolean(false);
  const submitButtonTitle = useMemo(
    () =>
      submitTitle
        ? submitTitle
        : hasMoreStep
        ? 'Continue'
        : isEdit
        ? 'Save'
        : 'Create',
    [submitTitle, hasMoreStep, isEdit]
  );

  const notificationMessage = useMemo(() => {
    if (isLabel) {
      return isEdit ? 'Label Name Updated' : 'Label Created Successfully';
    } else {
      return isEdit ? 'List Name Updated' : 'List Created Successfully';
    }
  }, [isLabel, isEdit]);

  const notificationDescription = useMemo(() => {
    if (isLabel) {
      return isEdit
        ? 'Name changed successfully.'
        : 'A new label has been added to your workspace.';
    } else {
      return isEdit
        ? 'Name changed successfully.'
        : 'A new list has been added to your workspace.';
    }
  }, [isLabel, isEdit]);

  const notificationIcon = useMemo(() => {
    return isLabel
      ? isEdit
        ? notificationIconMap.Label
        : notificationIconMap.AddLabel
      : isEdit
      ? notificationIconMap.List
      : notificationIconMap.AddList;
  }, [isEdit, isLabel]);

  const linkedinPlaceholder = useMemo(() => {
    return grouping === LabelGrouping.PEOPLE
      ? 'Enter the LinkedIn URL of the profile'
      : 'Enter the LinkedIn URL of the company';
  }, [grouping]);

  // Handlers
  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setName(value);
      if (value.length > 150) {
        setNameError(
          `${
            isLabel
              ? 'Label name must be 150 characters or fewer.'
              : 'List name must be 150 characters or fewer.'
          }`
        );
      } else if (value === defaultValue) {
        setNameError('Name already exists.');
      } else if (!value) {
        setNameError('Name is required');
      } else {
        setNameError('');
      }
      if (!value.trim()) {
        disabled.onTrue();
      } else if (isEdit && value === defaultValue) {
        disabled.onTrue();
      } else {
        disabled.onFalse();
      }
    },
    [defaultValue, isEdit, isLabel, disabled]
  );

  const handleChangeVisibility = useCallback((e: LabelVisibility) => {
    setVisibility(e);
  }, []);

  const handleChangeGrouping = useCallback(
    (value: LabelGrouping) => {
      setGrouping(value as LabelGrouping);
      const newInitialColor =
        value === LabelGrouping.COMPANY ? colors.primary : colors.success;
      if (!isLabel) setColor(newInitialColor);
    },
    [colors, setColor, setGrouping, isLabel]
  );
  const handleChangeLinkedinUrl = useCallback<
    NonNullable<TextFieldProps['onChange']>
  >(
    (event) => {
      const { value } = event.target;
      setLinkedinValue(value as string);
      if (linkedinError) {
        setLinkedinErrorText(
          grouping === LabelGrouping.COMPANY
            ? 'Please Enter a Valid LinkedIn Company URL.'
            : 'Please Enter a Valid LinkedIn People URL.'
        );
      } else {
        setLinkedinErrorText('');
      }
      if (hasLinkedinUrl && (!value.trim() || linkedinError)) {
        disabled.onTrue();
      } else if (hasLinkedinUrl && value.trim() && !linkedinError) {
        disabled.onFalse();
      }
    },
    [linkedinError, grouping, disabled, hasLinkedinUrl]
  );

  const handleBackToPreviousDialog = useCallback(() => {
    firstState?.onTrue();
    state.onFalse();
    onBack?.();
  }, [firstState, state, onBack]);

  const handleClose = useCallback(() => {
    firstState?.onTrue();
    state.onFalse();
    setLinkedinValue('');
    setLinkedinErrorText('');
    setName('');
    setNameError('');
    setDescription('');
    onClose?.();
    setColor(initialColor);
    setVisibility(LabelVisibility.INTERNAL);
  }, [firstState, state, onClose, initialColor, setColor, setVisibility]);

  const handleSubmit = useCallback(() => {
    if (hasMoreStep) {
      finalState?.onTrue();
      state.onFalse();
      onClick?.({ name, visibility });
    } else {
      if (type) {
        api
          .mutateAsync({
            type: isLabel ? LabelType.LABEL : LabelType.LIST,
            name: name.trim(),
            visibility,
            color: isLabel ? color : undefined,
            description,
            grouping,
          })
          .then((data: Label) => {
            if (shouldClearActionContext) {
              setLists([]);
              setLabels([]);
            } else {
              if (isLabel) {
                setLabels([...labels, data]);
              } else {
                setLists([...lists, data]);
              }
            }
            notify.complete(
              notificationMessage,
              notificationDescription,
              random(1, 101),
              {
                icon: notificationIcon,
              }
            );
            setVisibility(LabelVisibility.INTERNAL);
            setColor(initialColor);
            setDescription('');
            state.onFalse();
            firstState?.onTrue();
          });
      } else {
        onClick?.({
          name,
          visibility,
          color,
          description,
          grouping,
          linkedinValue,
        });
        if (hasLinkedinUrl) {
          setTimeout(() => {
            setLinkedinValue('');
            setLinkedinErrorText('');
            setGrouping(LabelGrouping.COMPANY);
          }, 1000);
        }
      }
    }
  }, [
    hasMoreStep,
    finalState,
    state,
    onClick,
    name,
    visibility,
    type,
    initialColor,
    color,
    description,
    grouping,
    notify,
    notificationMessage,
    notificationDescription,
    notificationIcon,
    firstState,
    linkedinValue,
    setLabels,
    setLists,
    labels,
    lists,
    hasLinkedinUrl,
  ]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(e.target.value);
    },
    []
  );

  const handleColorChange = useCallback((selectedColor: string) => {
    setColor(selectedColor);
  }, []);

  const handleAccessSettingsSubmit = useCallback(
    (presetVisibility: PresetVisibility, toggle: boolean) => {
      const labelVisibility =
        convertPresetVisibilityToLabelVisibility(presetVisibility);
      handleChangeVisibility(labelVisibility);
      setToggleVisibility(toggle);
    },
    [handleChangeVisibility]
  );

  // Effects
  useEffect(() => {
    if (!state.value) {
      setName(defaultValue || '');
      setNameError('');
    }
  }, [state, defaultValue]);

  useEffect(() => {
    if (hasLinkedinUrl) {
      if (name.trim()) {
        if (!linkedinValue.trim() || linkedinError) {
          disabled.onTrue();
        } else if (isEdit && name === defaultValue) {
          disabled.onTrue();
        } else {
          disabled.onFalse();
        }
      } else {
        if (!linkedinValue.trim() || linkedinError) {
          disabled.onTrue();
        } else {
          disabled.onFalse();
        }
      }
    } else {
      if (!name.trim()) {
        disabled.onTrue();
      } else if (isEdit && name === defaultValue && !toggleVisibility) {
        disabled.onTrue();
      } else if (nameError) {
        disabled.onTrue();
      } else {
        disabled.onFalse();
      }
    }
  }, [
    name,
    linkedinValue,
    linkedinError,
    hasLinkedinUrl,
    isEdit,
    defaultValue,
    disabled,
    toggleVisibility,
    nameError,
  ]);

  // Render
  return (
    <Dialog
      open={state.value}
      maxWidth="xs"
      fullWidth={true}
      TransitionComponent={Fade}
      onClose={handleClose}
      PaperProps={DIALOG_PAPER_PROPS}
    >
      <DialogTitle
        title={title || ''}
        icon={icon ?? 'chevron-left'}
        onClose={handleClose}
        onBack={onBack && handleBackToPreviousDialog}
      />

      <DialogContent sx={SX.dialogContent}>
        {!hasLinkedinUrl && (
          <Stack
            direction="row"
            gap={1}
            alignItems={nameError ? 'center' : 'flex-end'}
          >
            <OutlinedInput
              label={inputTitle}
              name="name"
              size="small"
              fullWidth
              placeholder={placeholder ?? 'Enter a New Name'}
              value={name}
              onChange={handleChangeName}
              required
              inputProps={{ maxLength: 151 }}
              error={!!nameError}
              helperText={nameError}
            />

            {hasColor && (
              <ActionBulkSelectColorLabel onChange={handleColorChange} />
            )}
          </Stack>
        )}

        {hasGrouping && (
          <SelectLabelGroupingStatus
            hasLinkedinUrl={hasLinkedinUrl}
            isLabel={isLabel}
            onChange={handleChangeGrouping}
          />
        )}

        {hasLinkedinUrl && (
          <OutlinedInput
            key="linkedin"
            label="LinkedIn URl:"
            name="linkedin"
            size="small"
            fullWidth
            value={linkedinValue}
            onChange={handleChangeLinkedinUrl}
            required
            placeholder={linkedinPlaceholder}
            error={linkedinError && !!linkedinValue}
            helperText={
              linkedinError && !!linkedinValue ? linkedinErrorText : ''
            }
          />
        )}
        {hasDescription && (
          <OutlinedInput
            sx={SX.descriptionInput}
            name="description"
            label="Description: (Optional)"
            placeholder="Enter Description"
            multiline
            minRows={4}
            maxRows={4}
            inputProps={{
              style: {
                overflowX: 'hidden',
                width: '100%',
                boxSizing: 'border-box',
              },
              'data-grammarly': 'false',
              spellCheck: false,
              autoCorrect: 'off',
              autoCapitalize: 'none',
              autoComplete: 'off',
            }}
            value={description}
            onChange={handleDescriptionChange}
          />
        )}

        {hasVisibility && (
          <AccessSettings onSubmit={handleAccessSettingsSubmit} item={item} />
        )}
      </DialogContent>

      <DialogActions
        submitTitle={submitButtonTitle}
        disabled={disabledSubmitButton ?? disabled.value}
        loading={api.isLoading ?? loading}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </Dialog>
  );
});

CreateActionBulkDialog.displayName = 'CreateActionBulkDialog';
